import React, { useEffect, useMemo, useState } from "react";
import { fetchCommodities, fetchCommodityDetail, fetchCommoditiesContextDeAt } from "@/lib/api";
import { MarketDisclaimerNote } from "@/components/DisclaimerBanner";
import DualExplainer from "@/components/DualExplainer";
import Sparkline from "@/components/Sparkline";
import {
  ChartLineUp,
  CaretUp,
  CaretDown,
  Minus,
  Plant,
  Grains,
  CircleNotch,
  CurrencyEur,
  Info,
} from "@phosphor-icons/react";

function trendIcon(pct) {
  if (pct == null) return <Minus size={12} className="text-zinc-500" />;
  if (pct > 0.5) return <CaretUp size={12} weight="fill" className="text-[#00FA9A]" />;
  if (pct < -0.5) return <CaretDown size={12} weight="fill" className="text-[#FF7A45]" />;
  return <Minus size={12} className="text-zinc-500" />;
}

function formatPct(pct) {
  if (pct == null) return "–";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

/**
 * Convert a CBOT/Euronext quote into EUR per metric ton for farmer-friendly
 * comparison with local selling prices. Returns null if the unit is unknown.
 * Common unit conventions on yfinance:
 *   "USc/bu" — US cents per bushel (Wheat/Corn/Soy/Oats)
 *   "USD/t"  — US dollars per metric ton (Soymeal)
 *   "USc/lb" — US cents per pound (Soy oil)
 *   "USD/cwt"— US dollars per hundredweight (Rough rice) — 1 cwt = 45.359 kg
 *   "CAD/t"  — Canadian dollars per ton (Canola) — approximated via USD
 */
const BUSHEL_KG = {
  "ZW": 27.216, // wheat 60 lb
  "ZC": 25.4,   // corn 56 lb
  "ZS": 27.216, // soy 60 lb
  "ZO": 14.515, // oats 32 lb
  "ZR": 45.359, // proxy only
};
function priceInEurPerTon(last, unit, eurPerUsd) {
  if (last == null || !unit || !eurPerUsd) return null;
  if (unit === "USc/bu") {
    // Infer grain type from our tickers — use wheat-kg as generic fallback (all ~27kg).
    const kgPerBushel = 27.216;
    const usdPerBushel = last / 100;
    const usdPerTon = (usdPerBushel / kgPerBushel) * 1000;
    return Math.round(usdPerTon * eurPerUsd);
  }
  if (unit === "USD/t") return Math.round(last * eurPerUsd);
  if (unit === "USc/lb") {
    const usdPerLb = last / 100;
    return Math.round(usdPerLb * 2204.62 * eurPerUsd);
  }
  if (unit === "USD/cwt") return Math.round(last * 22.046 * eurPerUsd);
  if (unit === "CAD/t") return Math.round(last * eurPerUsd * 0.74); // CAD≈0.74 USD approx
  return null;
}

export default function Commodities() {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState("wheat");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [horizon, setHorizon] = useState(30);
  const [ctx, setCtx] = useState(null);

  useEffect(() => { fetchCommodities().then(setCatalog).catch(() => {}); }, []);
  useEffect(() => {
    fetchCommoditiesContextDeAt().then(setCtx).catch(() => setCtx(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCommodityDetail(selected, { days: 180, horizon })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected, horizon]);

  const groups = catalog?.groups || [];
  const items = useMemo(() => catalog?.items || [], [catalog]);
  const byGroup = useMemo(() => {
    const m = {};
    for (const it of items) (m[it.group] ||= []).push(it);
    return m;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="commodities-root">
      <header>
        <div className="eid-label">Rohstoffmärkte · Grains &amp; Oilseeds</div>
        <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">
          Rohstoffe &amp; Prognosen
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Historische End-of-Day-Kurse + statistische Trend-Hochrechnung für
          {" "}{items.length} Rohstoffe aus Getreide &amp; Ölsaaten.
        </p>
      </header>

      <MarketDisclaimerNote />

      <DualExplainer
        title="Was sehe ich hier — und warum ist das wichtig?"
        testid="commodities-explainer"
        simple={
          <>
            <p>
              Das sind die <strong>Weltmarktpreise für die wichtigsten Ackerkulturen</strong> —
              so werden Weizen, Mais, Raps &amp; Co. an den großen Börsen (Chicago und Paris) gehandelt.
              Der Preis bestimmt, was du am Ende für deine Ernte bekommst.
            </p>
            <p>
              Die <strong>grüne Linie</strong> zeigt die vergangenen 6 Monate, die{" "}
              <strong>rote Linie</strong> unsere Computer-Hochrechnung für die nächsten 7, 30 oder 90 Tage.
              Der rote Schatten drumrum zeigt, wie unsicher die Vorhersage ist —
              je breiter der Bereich, desto unsicherer.
            </p>
            <p className="text-[#FF7A45]">
              <strong>Praxis-Tipp:</strong> Diese Zahlen sind <em>keine Empfehlung zu kaufen oder zu verkaufen</em>.
              Sprich vor großen Entscheidungen mit deinem Vermarkter oder Raiffeisen-Berater.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Datenquelle:</strong> Yahoo Finance (EOD, D+1 Delay), Fallback Alpha Vantage.
              Tickers: ZW=F (Wheat CBOT), ZC=F (Corn), ZS=F (Soybean), RS=F (Canola ICE Kanada),
              ZM=F (Soymeal), ZL=F (Soybean Oil), ZO=F (Oats), ZR=F (Rough Rice).
            </p>
            <p>
              <strong>Forecast-Modell:</strong> Hybrid OLS + AR(1) Residual + gedämpfte Saisonalität,
              Punktprognose + σ√h Konfidenzband (90 % CI, Z = 1.645).
              Keine exogenen Variablen (Wetter, Ernteberichte, Makro). Für ernsthaftes Modelling:
              SARIMA, Prophet oder LSTM mit Wetter- &amp; USDA-WASDE-Kovariaten empfohlen.
            </p>
            <p>
              <strong>Statistiken:</strong> Volatilität p.a. = σ(log-returns, n=30) · √252.
              Proxy-Ticker bei Gerste/Roggen/Sonnenblume (fehlt liquider Futures-Markt) — bitte
              Basisrisiko für lokale Preise selbst ergänzen.
            </p>
          </>
        }
      />

      {ctx && <DeAtContextPanel ctx={ctx} />}

      {/* Commodity picker grouped by category */}
      <section className="space-y-4" data-testid="commodities-picker">
        {groups.map((g) => (
          <div key={g.id} className="eid-card p-4">
            <div className="flex items-center gap-2 mb-3">
              {g.id === "cereals" ? <Grains size={18} className="text-[#00FA9A]" />
                : <Plant size={18} className="text-[#FF7A45]" />}
              <h2 className="text-base font-medium">{g.label}</h2>
              <span className="text-[10px] font-mono text-zinc-500">
                {(byGroup[g.id] || []).length}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {(byGroup[g.id] || []).map((it) => (
                <button
                  key={it.id}
                  onClick={() => setSelected(it.id)}
                  data-testid={`commodity-btn-${it.id}`}
                  className={`text-left border rounded-sm p-3 transition-all ${
                    selected === it.id
                      ? "border-white/15 shadow-sm bg-white/5"
                      : "border-white/10 hover:bg-white/5"
                  }`}
                >
                  <div className="text-sm font-medium">{it.emoji_de}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    {it.ticker} · {it.unit}
                    {it.proxy && <span className="ml-1 text-[#FFC24B]">(Proxy)</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Detail */}
      {loading && (
        <div className="eid-card p-8 flex items-center justify-center gap-2 text-zinc-400" data-testid="commodities-loading">
          <CircleNotch size={18} className="animate-spin" /> Lade Kursreihe…
        </div>
      )}

      {!loading && detail && <DetailSection detail={detail} horizon={horizon} setHorizon={setHorizon} ctx={ctx} />}
    </div>
  );
}

function DeAtContextPanel({ ctx }) {
  return (
    <section className="eid-card p-5" data-testid="commodities-deat-context">
      <div className="flex items-center gap-2 mb-3">
        <CurrencyEur size={18} className="text-[#00FA9A]" weight="bold" />
        <h2 className="text-base font-medium">DE & AT · Markt-Kontext für Bauern</h2>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">
          EUR/USD {ctx.fx?.usd_per_eur} · Stand {ctx.fx?.as_of}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(ctx.explainers || []).map((e, i) => (
          <div
            key={i}
            data-testid={`commodities-deat-explainer-${i}`}
            className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3"
          >
            <div className="flex items-start gap-2">
              <Info size={12} className="text-[#00FA9A] mt-1 shrink-0" weight="fill" />
              <div>
                <div className="text-sm font-medium">{e.title}</div>
                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{e.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DetailSection({ detail, horizon, setHorizon, ctx }) {
  const { commodity, series, forecast, stats } = detail;
  const combined = useMemo(() => {
    return [...(series.close || []), ...(forecast.point || [])];
  }, [series.close, forecast.point]);
  const historyCount = series.close?.length || 0;
  const eurPerTon = useMemo(() => priceInEurPerTon(stats?.last, commodity?.unit, ctx?.fx?.eur_per_usd), [stats?.last, commodity?.unit, ctx?.fx?.eur_per_usd]);

  return (
    <section className="eid-card p-5 space-y-5" data-testid="commodities-detail">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eid-label">{commodity.ticker} · {commodity.unit}</div>
          <h2 className="text-2xl font-medium tracking-tight mt-1">{commodity.label}</h2>
          {commodity.proxy && (
            <p className="text-xs text-[#FFC24B] mt-1">
              Kein eigener Liquide-Terminkontrakt verfügbar — Proxy über ähnliche Sorte.
            </p>
          )}
        </div>
        <div className="flex gap-1 text-xs" data-testid="commodities-horizon">
          {[7, 30, 90].map((h) => (
            <button
              key={h}
              onClick={() => setHorizon(h)}
              data-testid={`commodities-horizon-${h}`}
              className={`px-3 py-1.5 rounded-full border ${
                horizon === h
                  ? "bg-[#F5F5F5] text-[#0A0A0A] border-white/15"
                  : "border-white/10 hover:bg-white/5"
              }`}
            >
              {h} Tage
            </button>
          ))}
        </div>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="commodities-kpis">
        <Kpi label="Aktuell" value={stats.last?.toLocaleString("de-DE") ?? "–"}
             hint={commodity.unit} />
        <Kpi label="≈ EUR / t" value={eurPerTon != null ? `€${eurPerTon.toLocaleString("de-DE")}` : "–"}
             hint={eurPerTon != null ? "World-market proxy" : "Umrechnung n/a"}
             testid="kpi-eur-per-ton" />
        <Kpi label="7 Tage" value={formatPct(stats.change_7d_pct)}
             icon={trendIcon(stats.change_7d_pct)} />
        <Kpi label="30 Tage" value={formatPct(stats.change_30d_pct)}
             icon={trendIcon(stats.change_30d_pct)} />
        <Kpi label="Volatilität p.a." value={stats.volatility_30d_pct != null ? `${stats.volatility_30d_pct}%` : "–"} />
      </div>

      {/* Combined chart with forecast band */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">
            180 Tage Verlauf + {horizon}-Tage-Prognose
          </div>
          <ChartLineUp size={14} className="text-zinc-500" />
        </div>
        <ForecastChart
          history={series.close || []}
          forecast={forecast.point || []}
          upper={forecast.upper || []}
          lower={forecast.lower || []}
          historyCount={historyCount}
        />
        <div className="mt-2 flex items-center gap-4 text-[10px] font-mono text-zinc-400">
          <LegendDot color="#00FA9A" label="Historisch" />
          <LegendDot color="#FF5500" label="Prognose" />
          <LegendDot color="#C05A3E33" label="90 % Unsicherheitsband" solid />
          <span className="ml-auto text-zinc-500">
            Quelle: {series.source} · Modell: {forecast.model}
          </span>
        </div>
      </div>

      <div className="text-xs text-zinc-400 border-l-3 border-[#FFD700]/40 pl-3">
        {detail.disclaimer}
      </div>
    </section>
  );
}

function Kpi({ label, value, hint, icon, testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-3" data-testid={testid}>
      <div className="eid-label">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        {icon}
        <div className="text-lg font-medium font-mono">{value}</div>
      </div>
      {hint && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function LegendDot({ color, label, solid }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm ${solid ? "" : ""}`} style={{ background: color }} />
      {label}
    </span>
  );
}

function ForecastChart({ history, forecast, upper, lower, historyCount }) {
  const all = [...history, ...forecast, ...upper, ...lower].filter((v) => v != null);
  if (all.length === 0) return <div className="text-sm text-zinc-500">Keine Daten.</div>;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;

  const W = 720, H = 220, pad = 24;
  const totalPts = history.length + forecast.length;
  const stepX = (W - pad * 2) / Math.max(1, totalPts - 1);
  const y = (v) => pad + (1 - (v - min) / range) * (H - pad * 2);
  const x = (i) => pad + i * stepX;

  const histPts = history.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  // band polygon
  const bandPath = [
    ...upper.map((v, i) => `${x(historyCount + i)},${y(v)}`),
    ...lower.slice().reverse().map((v, i) => `${x(historyCount + lower.length - 1 - i)},${y(v)}`),
  ].join(" ");
  const fcPts = forecast.map((v, i) => `${x(historyCount + i)},${y(v)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="block">
      {/* Grid */}
      {[0.25, 0.5, 0.75].map((t) => (
        <line key={t} x1={pad} x2={W - pad} y1={pad + t * (H - pad * 2)} y2={pad + t * (H - pad * 2)}
              stroke="rgba(255,255,255,0.08)" strokeDasharray="2 4" />
      ))}
      {/* Band */}
      <polygon points={bandPath} fill="#FF5500" opacity="0.15" />
      {/* History line */}
      <polyline points={histPts} fill="none" stroke="#00FA9A" strokeWidth="1.8" />
      {/* Forecast line */}
      <polyline points={fcPts} fill="none" stroke="#FF5500" strokeWidth="1.8" strokeDasharray="4 3" />
      {/* Separator */}
      <line x1={x(historyCount - 1)} x2={x(historyCount - 1)} y1={pad} y2={H - pad}
            stroke="#8A8A93" strokeDasharray="1 3" />
      <text x={x(historyCount - 1) + 4} y={pad + 10} fontSize="10" fontFamily="monospace" fill="#8A8A93">
        jetzt
      </text>
      <text x={pad} y={H - 6} fontSize="10" fontFamily="monospace" fill="#8A8A93">
        min {min.toFixed(1)}
      </text>
      <text x={W - pad} y={pad + 4} fontSize="10" fontFamily="monospace" fill="#8A8A93" textAnchor="end">
        max {max.toFixed(1)}
      </text>
    </svg>
  );
}
